/**
 * Language Context
 * Fetches UI translations from backend with bundled JSON fallback.
 * Admin manages translations via admin panel → stored in MongoDB.
 * Arabic triggers RTL layout.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Bundled fallback translations (used when backend is unreachable)
import en from './en.json';
import fr from './fr.json';
import ar from './ar.json';
import es from './es.json';
import zh from './zh.json';
import pt from './pt.json';
import de from './de.json';
import ja from './ja.json';
import ru from './ru.json';
import it from './it.json';
import nl from './nl.json';
import tr from './tr.json';

const fallbackTranslations = { en, fr, ar, es, zh, pt, de, ja, ru, it, nl, tr };

const DEFAULT_LANGUAGES = [
  { code: 'en', label: 'EN', flag: 'EN', name: 'English' },
  { code: 'fr', label: 'FR', flag: 'FR', name: 'Français' },
  { code: 'ar', label: 'AR', flag: 'AR', name: 'العربية' },
  { code: 'es', label: 'ES', flag: 'ES', name: 'Español' },
  { code: 'zh', label: 'ZH', flag: 'ZH', name: '中文' },
  { code: 'pt', label: 'PT', flag: 'PT', name: 'Português' },
  { code: 'de', label: 'DE', flag: 'DE', name: 'Deutsch' },
  { code: 'ja', label: 'JA', flag: 'JA', name: '日本語' },
  { code: 'ru', label: 'RU', flag: 'RU', name: 'Русский' },
  { code: 'it', label: 'IT', flag: 'IT', name: 'Italiano' },
  { code: 'nl', label: 'NL', flag: 'NL', name: 'Nederlands' },
  { code: 'tr', label: 'TR', flag: 'TR', name: 'Türkçe' }
];

const API = process.env.REACT_APP_BACKEND_URL;

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    return localStorage.getItem('ui_language') || 'en';
  });
  const [dynamicTranslations, setDynamicTranslations] = useState({});
  const [availableLanguages, setAvailableLanguages] = useState(DEFAULT_LANGUAGES);
  const fetchedLangs = useRef(new Set());

  // Load available languages from backend settings
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const res = await fetch(`${API}/api/settings`);
        if (res.ok) {
          const settings = await res.json();
          if (settings.ui_languages && settings.ui_languages.length > 0) {
            const langs = settings.ui_languages.map(l => ({
              code: l.lang_code,
              label: l.lang_code.toUpperCase(),
              flag: l.flag || l.lang_code.toUpperCase(),
              name: l.lang_name,
              rtl: l.is_rtl || false
            }));
            setAvailableLanguages(langs);
          }
        }
      } catch (err) {
        // Use default languages on failure
      }
    };
    loadLanguages();
  }, []);

  // Fetch translations for current language from backend
  useEffect(() => {
    if (fetchedLangs.current.has(lang)) return;

    const loadTranslations = async () => {
      try {
        const res = await fetch(`${API}/api/ui-translations/${lang}`);
        if (res.ok) {
          const data = await res.json();
          setDynamicTranslations(prev => ({ ...prev, [lang]: data.translations }));
          fetchedLangs.current.add(lang);
        }
      } catch (err) {
        // Fallback to bundled JSON silently
      }
    };
    loadTranslations();
  }, [lang]);

  const setLang = useCallback((newLang) => {
    setLangState(newLang);
    localStorage.setItem('ui_language', newLang);
  }, []);

  // Keep layout always LTR - Arabic only changes text, not direction
  useEffect(() => {
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key) => {
    // Priority: dynamic (from DB) → bundled fallback for current lang → English fallback → key
    return dynamicTranslations[lang]?.[key]
      || fallbackTranslations[lang]?.[key]
      || fallbackTranslations.en?.[key]
      || key;
  }, [lang, dynamicTranslations]);

  // Always LTR layout
  const isRtl = false;

  return (
    <LanguageContext.Provider value={{ t, lang, setLang, isRtl, LANGUAGES: availableLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
